import {useEffect, useMemo, useRef} from 'react'
import {useQuery, useQueryClient} from '@tanstack/react-query'

import {ONE_MINUTE} from '#/lib/constants'
import {type QuickLoginStatusResponse} from '#/lib/wsoc/quicklogin/types'
import {extractQrTextFromImageUrl} from '#/wsocial/utils/qrCode'

export type QrSessionInitResult = {
  qrCodeUrl?: string
  sessionId?: string
  sessionToken?: string
  serviceId?: string
}

export type QrSessionStatusResult = QuickLoginStatusResponse

export interface UseQrSessionConfig {
  queryKey: string
  initFn: (
    signal: AbortSignal,
    prevServiceId?: string,
    prevSessionId?: string,
  ) => Promise<QrSessionInitResult>
  /** Pre-loaded session data — skips the initFn fetch entirely */
  initialData?: QrSessionInitResult
  statusFn?: (
    params: {sessionId: string; sessionToken: string},
    signal: AbortSignal,
  ) => Promise<QrSessionStatusResult>
  onCompleted?: (data: QrSessionStatusResult) => void
  onFailed?: (error?: string) => void
  retryOnError?: boolean
  pollingInterval?: number
  autoRefresh?: boolean
}

export interface QrSessionResult {
  qrCodeUrl?: string
  /** Decoded text extracted from the QR image; undefined while loading, null if extraction failed */
  qrCodeValue?: string | null
  sessionStatus?: 'completed' | 'pending' | 'failed'
  isInitLoading: boolean
  isInitSuccess: boolean
  isInitError: boolean
  initErrorMessage?: string
  isStatusError: boolean
  statusErrorMessage?: string
  refetchInit: () => void
}

export function useQrSession({
  queryKey,
  initFn,
  initialData,
  statusFn,
  onCompleted,
  onFailed,
  retryOnError = false,
  pollingInterval = 2000,
  autoRefresh = false,
}: UseQrSessionConfig): QrSessionResult {
  const queryClient = useQueryClient()

  const onCompletedRef = useRef(onCompleted)
  onCompletedRef.current = onCompleted
  const onFailedRef = useRef(onFailed)
  onFailedRef.current = onFailed
  const statusDataRef = useRef<QrSessionStatusResult | undefined>(undefined)
  const prevServiceIdRef = useRef<string | undefined>(undefined)
  const prevSessionIdRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    return () => {
      queryClient.cancelQueries({queryKey: [queryKey]})
      queryClient.removeQueries({queryKey: [queryKey]})
    }
  }, [queryClient, queryKey])

  const initQuery = useQuery({
    queryKey: [queryKey, 'init'],
    queryFn: ({signal}) =>
      initFn(signal, prevServiceIdRef.current, prevSessionIdRef.current),
    initialData,
    // skip the fetch when caller provides pre-loaded session data
    enabled: initialData === undefined,
  })

  useEffect(() => {
    if (retryOnError && initQuery.isError && !initQuery.isFetching) {
      initQuery.refetch()
    }
  }, [
    retryOnError,
    initQuery.isError,
    initQuery.isFetching,
    initQuery.refetch,
    initQuery,
  ])

  useEffect(() => {
    if (initQuery.data?.serviceId) {
      prevServiceIdRef.current = initQuery.data.serviceId
    }
    if (initQuery.data?.sessionId) {
      prevSessionIdRef.current = initQuery.data.sessionId
    }
  }, [initQuery.data?.serviceId, initQuery.data?.sessionId])

  const qrCodeUrl = useMemo(
    () => initQuery.data?.qrCodeUrl,
    [initQuery.data?.qrCodeUrl],
  )

  const qrValueQuery = useQuery({
    queryKey: [queryKey, 'qr-value', qrCodeUrl],
    queryFn: () => extractQrTextFromImageUrl(qrCodeUrl!),
    enabled: !!qrCodeUrl,
    staleTime: Infinity,
  })

  const sessionId = useMemo(
    () => initQuery.data?.sessionId,
    [initQuery.data?.sessionId],
  )
  const sessionToken = useMemo(
    () => initQuery.data?.sessionToken,
    [initQuery.data?.sessionToken],
  )
  const hasSession = useMemo(
    () => !!(sessionId && sessionToken && statusFn),
    [sessionId, sessionToken, statusFn],
  )

  const statusQuery = useQuery({
    queryKey: [queryKey, 'status', sessionId],
    queryFn: async ({signal}) => {
      if (!sessionId || !sessionToken || !statusFn) return undefined
      return statusFn({sessionId, sessionToken}, signal)
    },
    enabled: hasSession,
    refetchInterval: query => {
      const expiresAt = query.state.data?.expiresAt
      if (
        expiresAt &&
        new Date(expiresAt).getTime() - Date.now() <= ONE_MINUTE
      ) {
        return false
      }
      if (
        query.state.data?.status === 'completed' ||
        query.state.data?.status === 'failed' ||
        query.state.error
      ) {
        return false
      }
      return pollingInterval
    },
  })

  statusDataRef.current = statusQuery.data ?? undefined

  const isInvalidSession = useMemo(() => {
    if (statusQuery.error) {
      if (statusQuery.error?.name === 'WsocFetchError') {
        return statusQuery.error?.message === 'Invalid session'
      }
    }
    return false
  }, [statusQuery.error])

  const isExpiringSoon = useMemo(() => {
    const expiresAt = statusQuery.data?.expiresAt
    if (!expiresAt) return false
    return new Date(expiresAt).getTime() - Date.now() <= ONE_MINUTE
  }, [statusQuery.data])

  useEffect(() => {
    if (
      autoRefresh &&
      (isInvalidSession || isExpiringSoon) &&
      !initQuery.isFetching
    ) {
      queryClient.removeQueries({queryKey: [queryKey, 'init']})
      queryClient.removeQueries({queryKey: [queryKey, 'status']})
      initQuery.refetch()
    }
  }, [
    autoRefresh,
    isInvalidSession,
    isExpiringSoon,
    initQuery,
    initQuery.isFetching,
    queryClient,
    queryKey,
  ])

  useEffect(() => {
    if (!autoRefresh || !initQuery.isSuccess) return
    const timer = setTimeout(() => {
      if (statusDataRef.current?.status === 'completed') return
      queryClient.removeQueries({queryKey: [queryKey, 'status']})
      initQuery.refetch()
    }, 4 * ONE_MINUTE)
    return () => clearTimeout(timer)
    // dataUpdatedAt resets this timer whenever init data changes (including via the
    // isInvalidSession / isExpiringSoon path above), preventing a double-fetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, initQuery.isSuccess, initQuery.dataUpdatedAt])

  const completedStatus = useMemo(
    () => statusQuery.data?.status,
    [statusQuery.data],
  )
  const failedError = useMemo(() => statusQuery.data?.error, [statusQuery.data])

  useEffect(() => {
    if (completedStatus === 'completed') {
      const data = statusDataRef.current
      if (data) onCompletedRef.current?.(data)
    }
  }, [completedStatus])

  useEffect(() => {
    if (completedStatus === 'failed') {
      onFailedRef.current?.(failedError)
    }
  }, [completedStatus, failedError])

  const statusErrorMessage = statusQuery.isError
    ? (statusQuery.error as Error)?.message
    : statusQuery.data?.status === 'failed'
      ? statusQuery.data.error
      : undefined

  return {
    qrCodeUrl,
    qrCodeValue: qrValueQuery.data,
    sessionStatus: statusQuery.data?.status,
    isInitLoading: initQuery.isPending,
    isInitSuccess: initQuery.isSuccess,
    isInitError: initQuery.isError,
    initErrorMessage: (initQuery.error as Error)?.message,
    isStatusError: statusQuery.isError,
    statusErrorMessage,
    refetchInit: initQuery.refetch,
  }
}
